import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import { transformKeysToCamelCase } from "@/utils/decode/Decode";
import { formatToFloat } from "@/utils/transactions/FormatTransactionValue";
import {
  checkTransactionSenderReceiver,
  checkTransferType,
  checkTransactionStatus,
  checkXlsxTransactionDate,
  checkXlsxTransactionTime,
  checkTransactionValueForExport,
} from "@/utils/transactions/TransactionsUtils";

type Transaction = {
  id: string;
  categoryId?: string;
  value: string | number;
  description?: string;
  transactionType: string;
  rail?: string;
  transactionPostedDate?: string;
  createdAt?: string;
  createdTimestamp?: string;
  status?: string;
  transactionStatus?: string;
  type?: string;
  destinationAssetTypeId?: string;
  sourceAssetTypeId?: string;
  cpName?: string;
  sourceAccountIdId?: string;
  destinationAccountIdId?: string;
  sourceAccountId?: string;
  destinationAccountId?: string;
  sourceRegisteredName?: string;
  sourceFirstName?: string;
  destinationRegisteredName?: string;
  destinationFirstName?: string;
  amount?: string;
  amountToCredit?: string;
  amountToDebit?: string;
  originator?: {
    profile: {
      name: string;
    };
  };
  destination?: {
    profile?: {
      name: string;
    };
  };
  sourceDetails?: {
    id: string;
    assetTypeId: string;
    amountToDebit: number;
    sourceAccountId: {
      id: string;
      customer: {
        application: {
          customerDetails: {
            firstName?: string;
            registeredName?: string;
          };
        };
      };
    };
  };
  destinationDetails?: {
    id: string;
    assetTypeId: string;
    amountToDebit: number;
    destinationAccountId: {
      id: string;
      customer: {
        application: {
          customerDetails: {
            firstName?: string;
            registeredName?: string;
          };
        };
      };
    };
  };
};

export function generateStatement(
  transactions: Transaction[],
  currentAccountId: string,
  currentAccountAsset: string,
  t?: any,
  customerName?: string
) {
  const processedTransactions = transactions.map((transaction) => {
    return {
      ID: (transaction.id || transaction.categoryId) ?? "-",
      Date: checkXlsxTransactionDate(transaction),
      Time: checkXlsxTransactionTime(transaction),
      Status: checkTransactionStatus(transaction, t),
      Type: checkTransferType(
        transaction.transactionType,
        transaction.type,
        transaction.rail,
        t
      ),
      Originator:
        checkTransactionSenderReceiver(
          transaction,
          currentAccountId,
          false,
          true,
          false,
          customerName
        ) ?? "",
      Recipient:
        checkTransactionSenderReceiver(
          transaction,
          currentAccountId,
          false,
          false,
          true,
          customerName
        ) ?? "",
      Debit:
        checkTransactionValueForExport(transaction, currentAccountId, true) ??
        "",
      Credit:
        checkTransactionValueForExport(transaction, currentAccountId, false) ??
        "",
    };
  });

  const ws = XLSX.utils.json_to_sheet(processedTransactions);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bank Statement");

  const wbBlob = new Blob(
    [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );

  saveAs(wbBlob, "izziwallet_exported_transactions.xlsx");
}

export function generateAdminStatement(transactions: any[]) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  transactions.forEach((transaction) => {
    if (transaction.originator) {
      transaction.originator = transformKeysToCamelCase(transaction.originator);
    }
  });

  const exchangeTransactions = transactions.reduce((acc, transaction) => {
    if (transaction.exchangeRate) {
      acc.push({
        "Transaction ID": transaction.id ?? "-",
        "Transaction Date":
          formatDate(transaction.transactionDate || transaction.createdAt) ??
          "-",
        "Transaction Status": transaction.status ?? "-",
        "Exchange Rate": transaction.exchangeRate ?? "-",
        "Customer ID":
          transaction.sourceDetails?.sourceAccountId?.customer?.id ?? "-",
        "Customer First Name/Registered Name":
          (transaction.sourceDetails?.sourceAccountId?.customer?.application
            ?.customerDetailsCorporation?.registeredName ||
            transaction.sourceDetails?.sourceAccountId?.customer?.application
              ?.customerDetailsIndividual?.firstName) ??
          "-",
        "Customer Last Name/Trading Name":
          (transaction.sourceDetails?.sourceAccountId?.customer?.application
            ?.customerDetailsCorporation?.tradingName ||
            transaction.sourceDetails?.sourceAccountId?.customer?.application
              ?.customerDetailsIndividual?.lastName) ??
          "-",
        "Customer Type":
          transaction.sourceDetails?.sourceAccountId?.customer?.application
            ?.applicationType ?? "-",
        "Source Account ID":
          transaction.sourceDetails?.sourceAccountId?.id ?? "-",
        "Source Account Asset":
          transaction.sourceDetails?.sourceAccountId?.assetTypeId ?? "-",
        "Amount Debited": transaction.sourceDetails?.amountToDebit
          ? formatToFloat(transaction.sourceDetails.amountToDebit)
          : "-",
        "Destination Account ID": transaction.destinationDetails?.id ?? "-",
        "Destination Account Asset":
          transaction.destinationDetails?.assetTypeId ?? "-",
        "Amount Credited": transaction.destinationDetails?.amountToCredit
          ? formatToFloat(transaction.destinationDetails.amountToCredit)
          : "-",
        "Fee ID": transaction.fee?.transactionId ?? "-",
        "Fee Value": transaction.fee?.amount
          ? formatToFloat(transaction.fee.amount)
          : "-",
        "Fee Type": transaction.fee?.type ?? "-",
        "Fee Status": transaction.fee?.status ?? "-",
      });
    }
    return acc;
  }, []);

  const fiatDepositTransactions = transactions.reduce((acc, transaction) => {
    const splitOriginatorType =
      transaction.originator?.originatorType?.split("_");
    if (
      splitOriginatorType?.some((part: string) => part === "FIAT") &&
      (transaction.transactionType === "TRANSFER_IN" ||
        transaction.transactionType === "FIAT_TRANSFER_IN")
    ) {
      acc.push({
        "Transaction ID": transaction.id ?? "-",
        "Layer2 Transaction ID": transaction.layer2TransactionId ?? "-",
        "Transaction Date":
          formatDate(transaction.transactionDate || transaction.createdAt) ??
          "-",
        "Transaction Status": transaction.transactionStatus ?? "-",
        // eslint-disable-next-line prettier/prettier
        Rail: transaction.rail ?? "-",
        // eslint-disable-next-line prettier/prettier
        Value: transaction.value ?? "-",
        "Originator Name": transaction.originator?.profile?.name ?? "-",
        "Originator Bank Name":
          transaction.originator?.accountInformation?.institutionName ?? "-",
        "Originator Account Number":
          transaction.originator?.accountInformation?.accountNumber ?? "-",
        "Originator Routing Number":
          transaction.originator?.accountInformation?.routingNumber ?? "-",
        "Originator MEMO": transaction.originator?.railOriginatorMemo ?? "-",
        "Originator Address Line 1":
          transaction.originator?.profile?.address?.addressLine1 ?? "-",
        "Originator Country":
          (transaction.originator?.profile?.address?.country ||
            transaction.originator?.profile?.address?.countryCode) ??
          "-",
        "Originator State":
          transaction.originator?.profile?.address?.state ?? "-",
        "Originator City":
          transaction.originator?.profile?.address?.city ?? "-",
        "Originator ZIP Code":
          transaction.originator?.profile?.address?.postalCode ?? "-",
        "Originator Type": transaction.originator?.originatorType ?? "-",
        "Destination Account": transaction.account?.id ?? "-",
        "Destination Account Asset": transaction.account?.assetTypeId ?? "-",
        "Destination Customer ID": transaction.account?.customer?.id ?? "-",
        "Destination Customer First Name/Registered Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.registeredName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.firstName) ??
          "-",
        "Destination Customer Last Name/Trading Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.tradingName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.lastName) ??
          "-",
        "Destination Customer Type":
          transaction.account?.customer?.application?.applicationType ?? "-",
        "Fee ID": transaction.fee?.transactionId ?? "-",
        "Fee Value": transaction.fee?.amount
          ? formatToFloat(transaction.fee.amount)
          : "-",
        "Fee Type": transaction.fee?.type ?? "-",
        "Fee Status": transaction.fee?.status ?? "-",
      });
    }
    return acc;
  }, []);

  const cryptoDepositTransactions = transactions.reduce((acc, transaction) => {
    if (
      transaction.rail === "CRYPTO" &&
      (transaction.transactionType === "TRANSFER_IN" ||
        transaction.transactionType === "CRYPTO_TRANSFER_IN")
    ) {
      acc.push({
        "Transaction ID": transaction.id ?? "-",
        "Layer2 Transaction ID": transaction.layer2TransactionId ?? "-",
        "Blockchain Transaction ID":
          transaction.originator?.railReference ?? "-",
        "Transaction Date":
          formatDate(transaction.transactionDate || transaction.createdAt) ??
          "-",
        "Transaction Status": transaction.transactionStatus ?? "-",
        // eslint-disable-next-line prettier/prettier
        Rail: transaction.rail ?? "-",
        // eslint-disable-next-line prettier/prettier
        Value: transaction.value ?? "-",
        "Originator Institution Name":
          transaction.originator?.walletInformation?.institutionName ?? "-",
        "Originator Wallet Address":
          transaction.originator?.walletInformation?.blockchainAddress ?? "-",
        "Originator Blockchain":
          transaction.originator?.walletInformation?.blockchain ?? "-",
        "Originator Network":
          transaction.originator?.walletInformation?.network ?? "-",
        "Originator Type": transaction.originator?.originatorType ?? "-",
        "Destination Account": transaction.account?.id ?? "-",
        "Destination Account Asset": transaction.account?.assetTypeId ?? "-",
        "Destination Customer ID": transaction.account?.customer?.id ?? "-",
        "Destination Customer First Name/Registered Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.registeredName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.firstName) ??
          "-",
        "Destination Customer Last Name/Trading Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.tradingName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.lastName) ??
          "-",
        "Destination Customer Type":
          transaction.account?.customer?.application?.applicationType ?? "-",
        "Fee ID": transaction.fee?.transactionId ?? "-",
        "Fee Value": transaction.fee?.amount
          ? formatToFloat(transaction.fee.amount)
          : "-",
        "Fee Type": transaction.fee?.type ?? "-",
        "Fee Status": transaction.fee?.status ?? "-",
      });
    }
    return acc;
  }, []);

  const withdrawalTransactions = transactions.reduce((acc, transaction) => {
    if (
      (transaction.transactionType === "TRANSFER_OUT" ||
        transaction.transactionType === "FIAT_TRANSFER_OUT") &&
      transaction.rail !== "CRYPTO"
    ) {
      acc.push({
        "Transaction ID": transaction.id ?? "-",
        "Layer2 Transaction ID": transaction.layer2TransactionId ?? "-",
        "Transaction Date":
          formatDate(transaction.transactionDate || transaction.createdAt) ??
          "-",
        "Transaction Status": transaction.transactionStatus ?? "-",
        // eslint-disable-next-line prettier/prettier
        Rail: transaction.rail ?? "-",
        // eslint-disable-next-line prettier/prettier
        Value: transaction.value ?? "-",
        "Source Customer ID": transaction.account?.customer?.id ?? "-",
        "Source Customer First Name/Registered Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.registeredName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.firstName) ??
          "-",
        "Source Customer Last Name/Trading Name":
          (transaction.account?.customer?.application
            ?.customerDetailsCorporation?.tradingName ||
            transaction.account?.customer?.application
              ?.customerDetailsIndividual?.lastName) ??
          "-",
        "Source Customer Type":
          transaction.account?.customer?.application?.applicationType ?? "-",
        "Source Account ID": transaction.account?.id ?? "-",
        "Source Account Asset": transaction.account?.assetTypeId ?? "-",
        "Destination Counterparty ID":
          transaction.destinationCounterpartyId?.id ?? "-",
        "Destination Beneficiary Name":
          transaction.destinationCounterpartyId?.profile?.name ?? "-",
        "Destination Beneficiary Type":
          transaction.destinationCounterpartyId?.profile?.profileType ?? "-",
        "Destination Beneficiary Address":
          transaction.destinationCounterpartyId?.profile?.address
            ?.addressLine1 ?? "-",
        "Destination Beneficiary Country":
          transaction.destinationCounterpartyId?.profile?.address
            ?.countryCode ?? "-",
        "Destination Beneficiary State":
          transaction.destinationCounterpartyId?.profile?.address?.state ?? "-",
        "Destination Beneficiary City":
          transaction.destinationCounterpartyId?.profile?.address?.city ?? "-",
        "Destination Beneficiary ZIP Code":
          transaction.destinationCounterpartyId?.profile?.address?.postalCode ??
          "-",
        "Destination Bank Name":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionName ?? "-",
        "Destination Bank Address":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionAddress?.addressLine1 ?? "-",
        "Destination Bank Country":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionAddress?.countryCode ?? "-",
        "Destination Bank State":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionAddress?.state ?? "-",
        "Destination Bank City":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionAddress?.city ?? "-",
        "Destination Bank ZIP Code":
          transaction.destinationCounterpartyId?.accountInformation
            ?.institutionAddress?.postalCode ?? "-",
        "Destination Account Number":
          transaction.destinationCounterpartyId?.accountInformation
            ?.accountNumber ?? "-",
        "Destination Routing Number":
          transaction.destinationCounterpartyId?.accountInformation
            ?.routingNumber ?? "-",
        "Destination Swift BIC":
          transaction.destinationCounterpartyId?.accountInformation?.swiftBic ??
          "-",
        "Destination IBAN":
          transaction.destinationCounterpartyId?.accountInformation?.iban ??
          "-",
        "Transaction Purpose": transaction.purpose ?? "-",
        "Fee ID": transaction.fee?.transactionId ?? "-",
        "Fee Value": transaction.fee?.amount
          ? formatToFloat(transaction.fee.amount)
          : "-",
        "Fee Type": transaction.fee?.type ?? "-",
        "Fee Status": transaction.fee?.status ?? "-",
      });
    }
    return acc;
  }, []);

  const cryptoWithdrawalTransactions = transactions.reduce(
    (acc, transaction) => {
      if (
        (transaction.transactionType === "TRANSFER_OUT" ||
          transaction.transactionType === "FIAT_TRANSFER_OUT") &&
        transaction.rail === "CRYPTO"
      ) {
        acc.push({
          "Transaction ID": transaction.id ?? "-",
          "Layer2 Transaction ID": transaction.layer2TransactionId ?? "-",
          "Transaction Date":
            formatDate(transaction.transactionDate || transaction.createdAt) ??
            "-",
          "Transaction Status": transaction.transactionStatus ?? "-",
          // eslint-disable-next-line prettier/prettier
          Rail: transaction.rail ?? "-",
          // eslint-disable-next-line prettier/prettier
          Value: transaction.value ?? "-",
          "Source Customer ID": transaction.account?.customer?.id ?? "-",
          "Source Customer First Name/Registered Name":
            (transaction.account?.customer?.application
              ?.customerDetailsCorporation?.registeredName ||
              transaction.account?.customer?.application
                ?.customerDetailsIndividual?.firstName) ??
            "-",
          "Source Customer Last Name/Trading Name":
            (transaction.account?.customer?.application
              ?.customerDetailsCorporation?.tradingName ||
              transaction.account?.customer?.application
                ?.customerDetailsIndividual?.lastName) ??
            "-",
          "Source Customer Type":
            transaction.account?.customer?.application?.applicationType ?? "-",
          "Source Account ID": transaction.account?.id ?? "-",
          "Source Account Asset": transaction.account?.assetTypeId ?? "-",
          "Destination Counterparty ID":
            transaction.destinationCounterpartyId?.id ?? "-",
          "Destination Beneficiary Name":
            transaction.destinationCounterpartyId?.profile?.name ?? "-",
          "Destination Beneficiary Type":
            transaction.destinationCounterpartyId?.profile?.profileType ?? "-",
          "Destination Beneficiary Country":
            transaction.destinationCounterpartyId?.profile?.address
              ?.countryCode ?? "-",
          "Destination Wallet Address":
            transaction.destinationCounterpartyId?.walletInformation
              ?.blockchainAddress ?? "-",
          "Destination Wallet Type":
            transaction.destinationCounterpartyId?.walletInformation
              ?.walletType ?? "-",
          "Destination Institution Name":
            transaction.destinationCounterpartyId?.walletInformation
              ?.institutionName ?? "-",
          "Destination Institution Address":
            transaction.destinationCounterpartyId?.walletInformation
              ?.institutionAddress?.addressLine1 ?? "-",
          "Destination Institution Country":
            transaction.destinationCounterpartyId?.walletInformation
              ?.institutionAddress?.countryCode ?? "-",
          "Destination Institution State":
            transaction.destinationCounterpartyId?.walletInformation
              ?.institutionAddress?.state ?? "-",
          "Destination Institution City":
            transaction.destinationCounterpartyId?.walletInformation
              ?.institutionAddress?.city ?? "-",
          "Fee ID": transaction.fee?.transactionId ?? "-",
          "Fee Value": transaction.fee?.amount
            ? formatToFloat(transaction.fee.amount)
            : "-",
          "Fee Type": transaction.fee?.type ?? "-",
          "Fee Status": transaction.fee?.status ?? "-",
        });
      }
      return acc;
    },
    []
  );

  const internalTransfersTransactions = transactions.reduce(
    (acc, transaction) => {
      if (transaction.transactionType === "INTERNAL_TRANSFER") {
        acc.push({
          "Transaction ID": transaction.id ?? "-",
          "Transaction Date":
            formatDate(transaction.transactionDate || transaction.createdAt) ??
            "-",
          "Transaction Status":
            (transaction.transactionStatus || transaction.status) ?? "-",
          // eslint-disable-next-line prettier/prettier
          "Source Customer First Name/Registered Name":
            (transaction.sourceFirstName || transaction.sourceRegisteredName) ??
            "-",
          "Source Customer Last Name/Trading Name":
            (transaction.sourceLastName || transaction.sourceTradingName) ??
            "-",
          "Source Account ID":
            (transaction.sourceAccountIdId || transaction.sourceAccountId) ??
            "-",
          "Source Account Asset":
            (transaction.sourceAssetTypeId ||
              transaction.sourceAssetTypeIdId) ??
            "-",
          "Amount Sent": transaction.amountToDebit ?? "-",
          "Destination Customer First Name/Registered Name":
            (transaction.destinationFirstName ||
              transaction.destinationRegisteredName) ??
            "-",
          "Destination Customer Last Name/Trading Name":
            (transaction.destinationLastName ||
              transaction.destinationTradingName) ??
            "-",
          "Destination Account ID":
            (transaction.destinationAccountIdId ||
              transaction.destinationAccountId) ??
            "-",
          "Destination Account Asset":
            (transaction.destinationAssetTypeId ||
              transaction.destinationAssetTypeIdId) ??
            "-",
          "Amount Received": transaction.amountToCredit ?? "-",
        });
      }
      return acc;
    },
    []
  );

  const exchanges = XLSX.utils.json_to_sheet(exchangeTransactions);
  const fiatDeposits = XLSX.utils.json_to_sheet(fiatDepositTransactions);
  const fiatWithdrawals = XLSX.utils.json_to_sheet(withdrawalTransactions);
  const cryptoDeposits = XLSX.utils.json_to_sheet(cryptoDepositTransactions);
  const cryptoWithdrawals = XLSX.utils.json_to_sheet(
    cryptoWithdrawalTransactions
  );
  const internalTransfers = XLSX.utils.json_to_sheet(
    internalTransfersTransactions
  );

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, fiatWithdrawals, "FIAT Withdrawals");
  XLSX.utils.book_append_sheet(wb, fiatDeposits, "FIAT Deposits");
  XLSX.utils.book_append_sheet(wb, cryptoWithdrawals, "Crypto Withdrawals");
  XLSX.utils.book_append_sheet(wb, cryptoDeposits, "Crypto Deposits");
  XLSX.utils.book_append_sheet(wb, exchanges, "Exchanges");
  XLSX.utils.book_append_sheet(wb, internalTransfers, "Internal Transfers");

  const wbBlob = new Blob(
    [XLSX.write(wb, { bookType: "xlsx", type: "array" })],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );

  saveAs(wbBlob, "izziwallet_exported_transactions.xlsx");
}
